import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { IDS } from '@/constants/testIds';
import SwipeToDelete from '@/components/SwipeToDelete';
import { toast } from 'sonner';

export default function Cart() {
  const { user } = useAuth();
  const { refresh: refreshCart } = useCart();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [shipping, setShipping] = useState(user?.shipping_address || '');
  const [po, setPO] = useState('');
  const [submittedQuote, setSubmittedQuote] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cart');
      setCart(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateQty = async (product_id, qty) => {
    const items = cart.items.map((i) => ({ product_id: i.product_id, qty: i.product_id === product_id ? qty : i.qty }));
    const { data } = await api.put('/cart', { items });
    setCart(data);
    refreshCart();
  };

  const remove = async (product_id) => {
    const items = cart.items.filter((i) => i.product_id !== product_id).map((i) => ({ product_id: i.product_id, qty: i.qty }));
    const { data } = await api.put('/cart', { items });
    setCart(data);
    refreshCart();
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/quotes', { notes, shipping_address: shipping, po_number: po });
      setSubmittedQuote(data);
      await refreshCart();
      toast.success(`Quote ${data.quote_number} submitted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedQuote) {
    return (
      <div className="container-x py-24 max-w-2xl text-center" data-testid={IDS.cart.successState}>
        <span className="eyebrow" style={{ color: 'var(--ije-pink)' }}>Quote Received</span>
        <h1 className="h-display text-3xl md:text-5xl mt-4">Thank you.</h1>
        <p className="sans text-base text-neutral-600 mt-4 leading-relaxed">
          Your quote reference is <strong className="serif text-xl italic pink-gradient-text">{submittedQuote.quote_number}</strong>.
          An IJE HAUS rep will review and reach out to confirm terms and shipping.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/dashboard" className="btn-primary">View My Quotes <ArrowRight size={16} /></Link>
          <Link to="/products" className="btn-outline">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="container-x py-24 text-center sans text-xs tracking-widest uppercase text-neutral-500">Loading…</div>;
  }

  if (cart.items.length === 0) {
    return (
      <div className="container-x py-24 max-w-xl text-center" data-testid={IDS.cart.empty}>
        <h1 className="h-display text-3xl md:text-4xl">Your quote cart is empty.</h1>
        <p className="sans text-base text-neutral-500 mt-4">Add products from the catalog to build a quote request.</p>
        <Link to="/products" className="btn-primary mt-10 inline-flex">Browse Catalog</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-14" data-testid={IDS.cart.root}>
      <span className="eyebrow">Request for Quote</span>
      <h1 className="h-display text-3xl md:text-5xl mt-3">Your quote cart.</h1>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="border-t border-neutral-200">
            {cart.items.map((it) => (
              <SwipeToDelete key={it.product_id} onDelete={() => remove(it.product_id)}>
                <div className="grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr_auto] gap-4 items-center py-5 border-b border-neutral-100 bg-white" data-testid={IDS.cart.row(it.product_id)}>
                  <div className="bg-neutral-100 aspect-square overflow-hidden">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />}
                  </div>
                  <div className="min-w-0">
                    <p className="eyebrow truncate">{it.brand}</p>
                    <p className="serif text-base sm:text-lg mt-1 leading-snug">{it.name}</p>
                    <p className="sans text-xs text-neutral-500 mt-1 tracking-widest uppercase">
                      {it.sku} · Case {it.case_pack_size}
                    </p>
                    <p className="sans text-[0.6rem] text-neutral-400 mt-2 tracking-[0.15em] uppercase sm:hidden">
                      Swipe left to remove
                    </p>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <input
                        type="number"
                        min={it.min_order_qty}
                        step={it.case_pack_size}
                        value={it.qty}
                        onChange={(e) => updateQty(it.product_id, Math.max(it.min_order_qty, parseInt(e.target.value, 10) || it.min_order_qty))}
                        className="ije-input w-20 text-center"
                        data-testid={IDS.cart.qtyInput(it.product_id)}
                      />
                      <button type="button" onClick={() => remove(it.product_id)} className="sans text-xs text-neutral-500 hover:text-black hidden sm:flex items-center gap-1" data-testid={IDS.cart.remove(it.product_id)}>
                        <Trash2 size={14} /> Remove
                      </button>
                      <p className="serif text-base sm:text-lg sm:hidden ml-auto">${it.line_total.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="serif text-lg">${it.line_total.toFixed(2)}</p>
                    <p className="sans text-xs text-neutral-500 mt-1">${it.your_unit_price.toFixed(2)} × {it.qty}</p>
                  </div>
                </div>
              </SwipeToDelete>
            ))}
          </div>
        </div>

        <aside>
          <form onSubmit={submitQuote} className="border border-neutral-200 p-6 sticky top-24 space-y-5">
            <p className="eyebrow">Summary</p>
            <div className="flex justify-between border-b border-neutral-200 pb-4">
              <span className="sans text-sm text-neutral-500">Subtotal</span>
              <span className="serif text-2xl" data-testid={IDS.cart.subtotal}>${cart.subtotal.toFixed(2)}</span>
            </div>
            <div>
              <label className="ije-label">PO Number (optional)</label>
              <input value={po} onChange={(e) => setPO(e.target.value)} className="ije-input" data-testid={IDS.cart.poNumber} />
            </div>
            <div>
              <label className="ije-label">Shipping Address</label>
              <textarea rows={3} value={shipping} onChange={(e) => setShipping(e.target.value)} className="ije-input" data-testid={IDS.cart.shippingAddress} placeholder="Where should the supplier ship?" />
            </div>
            <div>
              <label className="ije-label">Notes to Rep (optional)</label>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="ije-input" data-testid={IDS.cart.notes} />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid={IDS.cart.submitQuote}>
              {submitting ? 'Submitting…' : 'Submit Quote Request'}
            </button>
            <p className="sans text-xs text-neutral-500 text-center">
              This is a request for quote — no payment is processed. An IJE HAUS rep will confirm terms.
            </p>
          </form>
        </aside>
      </div>
    </div>
  );
}
